import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaClipboardList,
  FaClock,
  FaCheckCircle,
  FaMoneyBillWave,
  FaSearch,
  FaCreditCard,
} from "react-icons/fa";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/Pagination";
import "./AdminOrderManagement.css";

const PER_PAGE = 10;

/** API uses orderStatus; older code used status */
const getOrderStatus = (o) => o?.orderStatus || o?.status || "pending";

const STATUS_FILTERS = [
  "all",
  "pending",
  "confirmed",
  "processing",
  "shipped",
  "delivered",
  "received",
  "completed",
  "cancelled",
];

const AdminOrderManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") {
          navigate("/");
        } else {
          fetchOrders();
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await api.get("/orders");
      setOrders(response.data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#FF9800",
      confirmed: "#2196F3",
      processing: "#9C27B0",
      shipped: "#00BCD4",
      delivered: "#43A047",
      received: "#2E7D32",
      completed: "#1B5E20",
      cancelled: "#F44336",
    };
    return colors[status] || "#666";
  };

  const filteredOrders = orders.filter((order) => {
    const s = getOrderStatus(order);
    const matchesStatus = filterStatus === "all" || s === filterStatus;
    const matchesSearch =
      order._id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.consumer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.farmer?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredOrders.length / PER_PAGE);
  const pageOrders = filteredOrders.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterStatus]);

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => getOrderStatus(o) === "pending").length,
    inProgress: orders.filter((o) =>
      ["confirmed", "processing", "shipped"].includes(getOrderStatus(o)),
    ).length,
    delivered: orders.filter((o) =>
      ["delivered", "received"].includes(getOrderStatus(o)),
    ).length,
    completed: orders.filter((o) => getOrderStatus(o) === "completed").length,
    cancelled: orders.filter((o) => getOrderStatus(o) === "cancelled").length,
    revenue: orders
      .filter((o) => o.paymentStatus === "paid")
      .reduce((sum, o) => sum + (o.totalPrice || 0), 0),
  };

  return (
    <AdminLayout>
      <div className="admin-order-management-content">
        {/* Page Header */}
        <div className="admin-order-management-page-header">
          <div>
            <h1 className="admin-order-management-page-title">
              {t("adminOrderManagement.title")}
            </h1>
            <p className="admin-order-management-page-subtitle">
              {t("adminOrderManagement.subtitle")}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="admin-order-management-stats-cards-grid">
          <div className="admin-order-management-stat-card">
            <div className="admin-order-management-stat-icon">
              <FaClipboardList aria-hidden />
            </div>
            <div className="admin-order-management-stat-content">
              <div className="admin-order-management-stat-value">
                {toLocalizedNumber(stats.total)}
              </div>
              <div className="admin-order-management-stat-label">
                {t("adminOrderManagement.totalOrders")}
              </div>
            </div>
          </div>
          <div className="admin-order-management-stat-card warning">
            <div className="admin-order-management-stat-icon">
              <FaClock aria-hidden />
            </div>
            <div className="admin-order-management-stat-content">
              <div className="admin-order-management-stat-value">
                {toLocalizedNumber(stats.inProgress)}
              </div>
              <div className="admin-order-management-stat-label">
                {t("adminOrderManagement.inProgress", "In progress")}
              </div>
            </div>
          </div>
          <div className="admin-order-management-stat-card">
            <div className="admin-order-management-stat-icon">
              <FaCheckCircle aria-hidden />
            </div>
            <div className="admin-order-management-stat-content">
              <div className="admin-order-management-stat-value">
                {toLocalizedNumber(stats.delivered + stats.completed)}
              </div>
              <div className="admin-order-management-stat-label">
                {t("adminOrderManagement.delivered")}
              </div>
            </div>
          </div>
          <div className="admin-order-management-stat-card highlight">
            <div className="admin-order-management-stat-icon">
              <FaMoneyBillWave aria-hidden />
            </div>
            <div className="admin-order-management-stat-content">
              <div className="admin-order-management-stat-value">
                Rs. {toLocalizedPrice(stats.revenue)}
              </div>
              <div className="admin-order-management-stat-label">
                {t("adminOrderManagement.totalRevenue")}
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="admin-order-management-filters-card">
          <div className="admin-order-management-search-box">
            <input
              type="text"
              placeholder={t("adminOrderManagement.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-order-management-search-input"
            />
            <FaSearch
              className="admin-order-management-search-icon"
              aria-hidden
            />
          </div>
          <div className="admin-order-management-filter-select-wrap">
            <label
              htmlFor="admin-order-status-filter"
              className="admin-order-management-filter-label"
            >
              {t("adminOrderManagement.status")}
            </label>
            <select
              id="admin-order-status-filter"
              className="admin-order-management-filter-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {STATUS_FILTERS.map((key) => (
                <option key={key} value={key}>
                  {key === "all"
                    ? `${t("adminUserManagement.allStatus", "All")} (${toLocalizedNumber(stats.total)})`
                    : `${toLocalizedStatus(key, t)} (${toLocalizedNumber(
                        orders.filter((o) => getOrderStatus(o) === key).length,
                      )})`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Orders Table */}
        <div className="admin-order-management-table-card">
          {loading ? (
            <div className="admin-order-management-loading-state">
              <div className="admin-order-management-spinner"></div>
              <p>{t("adminOrderManagement.loading")}</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="admin-order-management-empty-state">
              <div className="admin-order-management-empty-icon">
                <FaClipboardList aria-hidden />
              </div>
              <h3>{t("adminOrderManagement.noOrders")}</h3>
              <p>{t("adminOrderManagement.tryAdjusting")}</p>
            </div>
          ) : (
            <div className="admin-order-management-table-container">
              <table className="admin-order-management-data-table">
                <thead>
                  <tr>
                    <th>{t("adminOrderManagement.orderId")}</th>
                    <th>{t("adminOrderManagement.consumer")}</th>
                    <th>{t("adminOrderManagement.farmer")}</th>
                    <th>{t("adminOrderManagement.items")}</th>
                    <th>{t("adminOrderManagement.totalPrice")}</th>
                    <th>{t("adminOrderManagement.paymentMethod")}</th>
                    <th>{t("adminOrderManagement.status")}</th>
                    <th>{t("adminOrderManagement.date")}</th>
                    <th>{t("adminOrderManagement.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOrders.map((order) => (
                    <tr key={order._id}>
                      <td>#{order._id?.slice(-8) || "N/A"}</td>
                      <td>
                        <div className="admin-order-management-user-info-small">
                          <div className="admin-order-management-user-name">
                            {order.consumer?.name || "N/A"}
                          </div>
                          <div className="admin-order-management-user-email">
                            {order.consumer?.email || ""}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/users/${order.farmer?.username || order.farmer?._id || "1"}`}
                          className="admin-order-management-farmer-link"
                        >
                          {order.farmer?.name || "N/A"}
                        </Link>
                      </td>
                      <td>
                        <div className="admin-order-management-items-list">
                          {order.items?.slice(0, 2).map((item, idx) => (
                            <div
                              key={idx}
                              className="admin-order-management-item-name"
                            >
                              {item.name || item.product?.name || "—"} x
                              {toLocalizedNumber(item.quantity)}
                            </div>
                          ))}
                          {order.items?.length > 2 && (
                            <div className="admin-order-management-more-items">
                              +{toLocalizedNumber(order.items.length - 2)} more
                            </div>
                          )}
                        </div>
                      </td>
                      <td>Rs. {toLocalizedPrice(order.totalPrice || 0)}</td>
                      <td>
                        <span className="admin-order-management-payment-method-badge">
                          {(order.paymentMethod || "").toLowerCase() ===
                          "cod" ? (
                            <>
                              <FaMoneyBillWave aria-hidden /> COD
                            </>
                          ) : (
                            <>
                              <FaCreditCard aria-hidden /> Online
                            </>
                          )}
                        </span>
                      </td>
                      <td>
                        <span
                          className="admin-order-management-status-badge"
                          style={{
                            background: `${getStatusColor(getOrderStatus(order))}20`,
                            color: getStatusColor(getOrderStatus(order)),
                          }}
                        >
                          {toLocalizedStatus(getOrderStatus(order), t)}
                        </span>
                      </td>
                      <td>{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td className="admin-order-management-actions-cell">
                        <Link
                          to={`/admin/orders/${order._id}`}
                          className="admin-order-management-action-btn view"
                        >
                          {t("adminOrderManagement.viewDetails")}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminOrderManagement;
